import React, { useEffect, useMemo, useState } from 'react';
import SelectButton from 'components/atoms/select_button';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  DocumentData,
  DocumentsGroup,
  DocumentsState,
  DocumentsTypes,
} from 'store/modules/documents_store/types';
import Loader from 'components/atoms/loader';
import _ from 'underscore';
import DocumentItem from 'components/molecules/document_item';
import ModalAuthenticationDocument from 'components/atoms/modal_authentication_document';
import { Container } from './styles';

interface DocumentsListProps {
  selectedDocuments: Array<number>;
  setSelectedDocuments: React.Dispatch<React.SetStateAction<number[]>>;
  documents: Array<DocumentData>;
  documentGroupList: Array<DocumentsGroup>;
  setModalAuthenticateDocumentoOpened: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentDocumentId: React.Dispatch<React.SetStateAction<string>>;
  setShowModalWarningAdditional: React.Dispatch<React.SetStateAction<boolean>>;
  setDocumentSelectedForPreview: React.Dispatch<React.SetStateAction<DocumentData | null>>;
}

const DocumentsList = (props: DocumentsListProps) => {
  const {
    selectedDocuments,
    setSelectedDocuments,
    documents,
    documentGroupList,
    setModalAuthenticateDocumentoOpened,
    setCurrentDocumentId,
    setShowModalWarningAdditional,
    setDocumentSelectedForPreview,
  } = props;

  const [showModalAuthenticationStatus, setShowModalAuthenticationStatus] =
    useState<boolean>(false);

  const [authenticationRequestSuccess, setAuthenticationRequestSuccess] = useState<boolean>(false);

  const { request } = useSelector<ApplicationState, DocumentsState>(
    (state) => state.session.documents,
  );

  const dispatch = useDispatch();

  const SHORT_DELAY = 2000;

  useEffect(() => {
    if (request?.authenticate_document?.loaded === true) {
      setShowModalAuthenticationStatus(true);
      setAuthenticationRequestSuccess(true);
    }
    if (request?.authenticate_document?.error === true) {
      setShowModalAuthenticationStatus(true);
      setAuthenticationRequestSuccess(false);
    }
  }, [request.authenticate_document]);

  useEffect(() => {
    if (!showModalAuthenticationStatus) return;

    setTimeout(() => {
      setShowModalAuthenticationStatus(false);
      dispatch({ type: DocumentsTypes.SET_AUTHENTICATE_DOCUMENT_INITIAL_STATE });
    }, SHORT_DELAY);
  }, [showModalAuthenticationStatus]);

  const getDocumentId = (document: DocumentData): number =>
    Number(document?.default?.id || document?.id);

  const wholePageSelected = useMemo(
    () =>
      documents
        ?.filter((doc) => doc.fileUrl)
        .filter((doc) => new Date(doc.expirationDate).getTime() > Date.now())
        .every((doc) => selectedDocuments.includes(getDocumentId(doc))),
    [documents, selectedDocuments],
  );

  const toggleSelectedDocuments = (id: number, updateAll = false) => {
    if (updateAll) {
      const allDocumentIds = documents.filter((doc) => doc.fileUrl).map(getDocumentId);
      setSelectedDocuments(
        wholePageSelected
          ? selectedDocuments.filter((docId) => !allDocumentIds.includes(docId))
          : _.uniq([...selectedDocuments, ...allDocumentIds]),
      );
    } else {
      setSelectedDocuments((prevSelected) =>
        prevSelected.includes(id)
          ? prevSelected.filter((docId) => docId !== id)
          : [...prevSelected, id],
      );
    }
  };

  return (
    <Container>
      <div>
        {request?.get_documents?.loading || request?.filter_documents?.loading ? (
          <Loader />
        ) : documents?.length === 0 ? (
          <div className="text-center py-4">Nenhum documento encontrado.</div>
        ) : (
          <div className="container-fluid">
            <div className="row bg-light py-2 border-bottom font-weight-bold align-items-center">
              <div className="col-1 text-center">
                <SelectButton
                  id={0}
                  isChecked={wholePageSelected}
                  text=""
                  toggleSelected={(id) => toggleSelectedDocuments(id, true)}
                />
              </div>

              <div className="col-2">Documento</div>
              <div className="col-1 text-center">Tipo</div>
              <div className="col-2 text-center">Grupo</div>
              <div className="col-1 text-center">Situação</div>
              <div className="col-2 text-center">Vencimento</div>
              <div className="col-2 text-center">Autenticação</div>
              <div className="col-1 text-center">Ações</div>
            </div>
            {documents?.map((document) => (
              <DocumentItem
                key={document.id}
                document={document}
                documentGroupList={documentGroupList}
                toggleSelectedDocuments={toggleSelectedDocuments}
                selectedDocuments={selectedDocuments}
                setModalAuthenticateDocumentoOpened={setModalAuthenticateDocumentoOpened}
                setCurrentDocumentId={setCurrentDocumentId}
                setShowModalWarningAdditional={setShowModalWarningAdditional}
                setDocumentSelectedForPreview={setDocumentSelectedForPreview}
              />
            ))}
          </div>
        )}
      </div>
      {showModalAuthenticationStatus && (
        <ModalAuthenticationDocument
          authenticationSuccess={authenticationRequestSuccess}
          showModalAuthenticationStatus={showModalAuthenticationStatus}
          setshowModalAuthenticationStatus={setShowModalAuthenticationStatus}
        />
      )}
    </Container>
  );
};

export default DocumentsList;
